import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiService } from "../services/api";
import { queryKeys } from "../lib/queryKeys";
import { getApiErrorMessage } from "../lib/apiErrors";
import type { TicketFollowersResponse, FollowTicketResponse } from "../types/tickets";

/**
 * Hook to get followers for a ticket
 */
export function useTicketFollowers(ticketId: string | undefined, organizationId: string | undefined) {
  return useQuery({
    queryKey: [...queryKeys.tickets.followers(ticketId!), organizationId],
    queryFn: async () => {
      const response = await apiService.getTicketFollowers(ticketId!, organizationId);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch followers'));
      }
      return response.data as TicketFollowersResponse;
    },
    enabled: !!ticketId && !!organizationId,
    staleTime: 30000,
  });
}

/**
 * Hook to follow a ticket
 */
export function useFollowTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ticketId, organizationId }: { ticketId: string; organizationId?: string }) => {
      const response = await apiService.followTicket(ticketId, organizationId);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to follow ticket'));
      }
      return response.data as FollowTicketResponse;
    },
    onSuccess: (_, { ticketId }) => {
      // Invalidate followers query
      queryClient.invalidateQueries({
        queryKey: queryKeys.tickets.followers(ticketId),
      });
      // Also invalidate history so Activity History updates
      queryClient.invalidateQueries({
        queryKey: queryKeys.tickets.history(ticketId),
      });
    },
  });
}

/**
 * Hook to unfollow a ticket
 */
export function useUnfollowTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ticketId, organizationId }: { ticketId: string; organizationId?: string }) => {
      const response = await apiService.unfollowTicket(ticketId, organizationId);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to unfollow ticket'));
      }
      return response.data as FollowTicketResponse;
    },
    onSuccess: (_, { ticketId }) => {
      // Invalidate followers query
      queryClient.invalidateQueries({
        queryKey: queryKeys.tickets.followers(ticketId),
      });
      // Also invalidate history so Activity History updates
      queryClient.invalidateQueries({
        queryKey: queryKeys.tickets.history(ticketId),
      });
    },
  });
}

/**
 * Hook to toggle follow status on a ticket
 */
export function useToggleTicketFollow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ticketId, organizationId }: { ticketId: string; organizationId?: string }) => {
      const response = await apiService.toggleTicketFollow(ticketId, organizationId);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to toggle follow'));
      }
      return response.data as FollowTicketResponse;
    },
    onSuccess: (_, { ticketId }) => {
      // Invalidate followers query
      queryClient.invalidateQueries({
        queryKey: queryKeys.tickets.followers(ticketId),
      });
      // Also invalidate history so Activity History updates
      queryClient.invalidateQueries({
        queryKey: queryKeys.tickets.history(ticketId),
      });
    },
  });
}
